import {useEffect, useRef} from "react";
import {ensureFresh, KEEPALIVE_MIN_VALIDITY_SECONDS} from "./authSession";

/*
 * CANONICAL IMPLEMENTATION - source of truth is magic-penguin,
 * src/app/components/auth/session-keepalive.service.ts. Keep the two in step.
 */

/** How often we check whether the token is worth refreshing. */
const TICK_MS = 60_000;

/**
 * How recently the user must have touched the browser for a tick to refresh. Deliberately wider
 * than TICK_MS so that a single quiet tick does not drop someone who is still working.
 */
const ACTIVITY_WINDOW_MS = 2 * TICK_MS;

/** Cheap, high-signal evidence that the user is still at the keyboard. */
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];

/**
 * Keeps the access token alive while the user works purely in the UI - filling in a test set,
 * writing a prompt - without sending anything to the backend. The axios interceptor covers
 * everything that does reach the backend; this covers everything that does not, where the token
 * would otherwise expire and the SSO session idle out mid-edit, costing the user unsaved work.
 *
 * Refreshing is gated on real user activity on purpose. A bare timer would hold the SSO session
 * open indefinitely from a forgotten tab and defeat the realm's idle timeout.
 */
export function useSessionKeepalive() {
    // A ref rather than state: activity must not re-render anything, or every scroll event would
    // cost a render pass.
    const lastActivityAt = useRef(0);

    useEffect(() => {
        const markActive = () => {
            lastActivityAt.current = Date.now();
        };

        for (const event of ACTIVITY_EVENTS) {
            // capture: true because scroll does not bubble up from inner scroll containers.
            document.addEventListener(event, markActive, {passive: true, capture: true});
        }

        const tick = window.setInterval(async () => {
            // Zero means "never": opening a tab is not activity, so an abandoned one must not hold
            // the SSO session open.
            if (Date.now() - lastActivityAt.current > ACTIVITY_WINDOW_MS) {
                return;
            }

            try {
                // No-op unless the token is within the threshold of expiring, so a user who is
                // present but idle causes one refresh per token lifespan, not one per tick.
                await ensureFresh(KEEPALIVE_MIN_VALIDITY_SECONDS);
            } catch {
                // The SSO session is gone; the login redirect is already under way.
            }
        }, TICK_MS);

        return () => {
            window.clearInterval(tick);
            for (const event of ACTIVITY_EVENTS) {
                document.removeEventListener(event, markActive, {capture: true});
            }
        };
    }, []);
}
